import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { getRankInfo } from '../rank';

const API = process.env.REACT_APP_API_URL || '';

const STATE_NAMES = {
  AL:'Alabama',AK:'Alaska',AZ:'Arizona',AR:'Arkansas',CA:'California',CO:'Colorado',
  CT:'Connecticut',DE:'Delaware',FL:'Florida',GA:'Georgia',HI:'Hawaii',ID:'Idaho',
  IL:'Illinois',IN:'Indiana',IA:'Iowa',KS:'Kansas',KY:'Kentucky',LA:'Louisiana',
  ME:'Maine',MD:'Maryland',MA:'Massachusetts',MI:'Michigan',MN:'Minnesota',MS:'Mississippi',
  MO:'Missouri',MT:'Montana',NE:'Nebraska',NV:'Nevada',NH:'New Hampshire',NJ:'New Jersey',
  NM:'New Mexico',NY:'New York',NC:'North Carolina',ND:'North Dakota',OH:'Ohio',OK:'Oklahoma',
  OR:'Oregon',PA:'Pennsylvania',RI:'Rhode Island',SC:'South Carolina',SD:'South Dakota',
  TN:'Tennessee',TX:'Texas',UT:'Utah',VT:'Vermont',VA:'Virginia',WA:'Washington',
  WV:'West Virginia',WI:'Wisconsin',WY:'Wyoming',DC:'D.C.',
};

function timeAgo(dateStr) {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

export default function PublicProfile() {
  const { userId } = useParams();
  const { user: me } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (me && String(me.id) === String(userId)) {
      navigate('/profile', { replace: true });
      return;
    }
    Promise.all([
      axios.get(`${API}/api/user/${userId}`),
      axios.get(`${API}/api/user/${userId}/matches`),
    ])
      .then(([p, m]) => { setProfile(p.data); setMatches(m.data); })
      .catch(() => setError('Player not found.'))
      .finally(() => setLoading(false));
  }, [userId]); // eslint-disable-line

  if (loading) return (
    <div className="profile-page">
      <div className="profile-card" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 200 }}>
        <div className="queue-spinner" />
      </div>
    </div>
  );

  if (error) return (
    <div className="profile-page">
      <div className="profile-card" style={{ textAlign: 'center', color: 'var(--cb-gray-500)', padding: 48 }}>
        {error}
      </div>
    </div>
  );

  const rank = getRankInfo(profile.rating ?? 0);
  const location = [
    profile.school_county ? profile.school_county.replace(/\b\w/g, c => c.toUpperCase()) + ' Co.' : null,
    profile.school_state ? (STATE_NAMES[profile.school_state] || profile.school_state) : null,
  ].filter(Boolean).join(', ');

  return (
    <div className="profile-page">
      <div className="profile-card">

        {/* Header */}
        <div className="profile-header">
          <div className="profile-avatar-wrap" style={{ cursor: 'default' }}>
            {profile.avatar
              ? <img src={profile.avatar} alt="" className="profile-avatar" />
              : <div className="profile-avatar profile-avatar-initials">
                  {(profile.display_name || '?').charAt(0).toUpperCase()}
                </div>
            }
          </div>
          <div className="profile-header-info">
            <div className="pub-profile-name">{profile.display_name}</div>
            <div className="profile-rank-row">
              <img src={rank.icon} alt={rank.label} className="profile-rank-icon" />
              <span className="profile-rank-label" style={{ color: rank.color }}>{rank.label}</span>
            </div>
            {/* XP hidden on public profiles */}
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 4 }}>
              {profile.grade_year && <div className="profile-grade">{profile.grade_year}</div>}
              {profile.school && <div className="profile-grade" style={{ background: '#eef2ff', color: '#4338ca' }}>{profile.school}</div>}
            </div>
            {location && <div className="pub-profile-location">{location}</div>}
          </div>
        </div>

        {/* Stats */}
        <div className="profile-stats-row">
          <div className="profile-stat"><div className="profile-stat-val">{profile.login_streak ?? 0}</div><div className="profile-stat-lbl">Login Streak</div></div>
          {profile.sat_score && (
            <div className="profile-stat"><div className="profile-stat-val">{profile.sat_score}</div><div className="profile-stat-lbl">SAT Score</div></div>
          )}
        </div>

        {/* Bio */}
        {profile.bio && (
          <>
            <div className="profile-divider" />
            <div className="pub-profile-bio">{profile.bio}</div>
          </>
        )}

        {/* Recent Matches */}
        {matches.length > 0 && (
          <>
            <div className="profile-divider" />
            <div className="pub-matches-title">Recent Matches</div>
            <div className="pub-matches-list">
              {matches.map(m => {
                const oppRank = getRankInfo(m.opp_rating ?? 0);
                const resultLabel = m.result === 'win' ? 'W' : m.result === 'loss' ? 'L' : 'D';
                const deltaSign = m.delta >= 0 ? '+' : '';
                return (
                  <div key={m.id} className="pub-match-row">
                    <span className={`pub-match-result ${m.result}`}>{resultLabel}</span>
                    <div className="pub-match-info">
                      <img src={oppRank.icon} alt={oppRank.label} className="pub-match-rank-icon" title={oppRank.label} />
                      <span className="pub-match-opp" style={{ color: oppRank.color }}>{m.opp_name}</span>
                      <span className="pub-match-score">{m.my_score}–{m.opp_score}</span>
                    </div>
                    <span className="pub-match-ago">{timeAgo(m.created_at)}</span>
                    <span className={`pub-match-delta ${m.delta >= 0 ? 'pos' : 'neg'}`}>{m.delta > 0 ? '+' : ''}{m.delta} XP</span>
                  </div>
                );
              })}
            </div>
          </>
        )}

      </div>
    </div>
  );
}
